import { Paginated } from "@feathersjs/feathers/lib";
import { DataType, ICompany, ICourse, ICourseUser, IProgram, IProgramUser, IProgress, IUser, Language, Services, StatisticsQuery, StatisticsResult, UserType } from "types";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import i18next from "i18n";
import client from "../feathers";
import { Company, Course, Data, Program } from "./";
import { authStore } from "app";
import { paramsForServer } from "feathers-hooks-common";
import { Curriculum } from "./curriculum";

export class User extends Data<IUser> implements IUser {
  email:            string = null;
  first_name:       string = null;
  last_name:        string = null;
  password:         string = null;
  personal_number:  string = null;
  phone:            string = null;
  title:            string = null;
  user_type:        UserType  = null;
  language:         Language  = null;
  progress                    = 0;
  programs?:        Program[] = [];
  courses?:         Course[]  = [];
  company?:         ICompany  = null;
  external_editor?: boolean   = false;
  statistics?:      StatisticsResult = {};

  constructor(data: IUser) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.User, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually
    makeObservable(this, {
      email:           observable,
      first_name:      observable,
      last_name:       observable,
      password:        observable,
      personal_number: observable,
      phone:           observable,
      title:           observable,
      user_type:       observable,
      language:        observable,
      programs:        observable,
      courses:         observable,
      progress:        observable,
      company:         observable,
      statistics:      observable,
      external_editor: observable,

      getFullName: computed,

      setCompany: action,
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here.
    this.fromData(data);
  }

  /*******************************************************************************************************************************************
   * Database Access
   ********************************************************************************************************************************************/
  /**
   * Load all courses the current user is taking and store them in @this.courses
   */
  public loadCourses = async (): Promise<Course[]> => {
    try {
      const course_users: Paginated<ICourseUser> = await client.service(Services.CourseUser).find({
        query: {
          $select: ["course_id", "progress", "rating", "rating_last_updated_at"],
          user_id: this.id
        }
      });
      const courses: Paginated<ICourse> = await client.service(Services.Course).find({
        query: {
          id: { $in: course_users.data.map(u => u.course_id) }
        }
      });
      runInAction(() => this.courses = courses.data.map(c => Course.create(c)));
      return this.courses.map(c => {
        const course_user = course_users.data.find(cu => Number(cu.course_id) === Number(c.id))
        runInAction(() => {
          [c.progress, c.rating, c.rating_last_updated_at] = [course_user.progress, course_user.rating, course_user.rating_last_updated_at];
        });
        return c;
      });
    } catch(err) {
      console.error("Failed to load user courses", err);
      return [];
    }
  }

  /**
   * Load all programs the current user is taking and store them in @this.programs
   */
  public loadPrograms = async (): Promise<Program[]> => {
    try {
      const program_users: Paginated<IProgramUser> = await client.service(Services.ProgramUser).find({
        $select: ["program_id"],
        user_id: this.id
      });
      const programs: Paginated<IProgram> = await client.service(Services.Program).find({
        id: { $in: program_users.data.map(u => u.program_id) }
      });
      runInAction(() => this.programs = programs.data.map(p => Program.create(p)));
      return this.programs;
    } catch(err) {
      console.error("Failed to load user programs", err);
      return [];
    }
  }

  /**
   * Find all users of the current users company that are taking @content_id of type @content_type
   * 
   * @param curriculum_type 
   * @param curriculum 
   * 
   * Note: For managers and admins
   */
  public static getParticipants = async (curriculum_type: DataType.Course | DataType.Program | DataType.Package, curriculum: Curriculum, company_id?: number): Promise<User[]> => {
    try {
      if(curriculum_type === DataType.Package) {
        const programs: Program[] = await Program.get(undefined, true, curriculum) as Program[];
        const users: User[] = [];
        for(const p of programs) {
          users.push(...(await User.getParticipants(DataType.Program, p) as User[]));
        }
        return [...users].filter((u, idx) => users.findIndex(_u => u.id === _u.id) === idx);
      } else {
        let users: User[];
        if(company_id) {
          const company: Company = await Company.get(company_id) as Company;
          if(!company) throw new Error("Failed to load company");
          await company.loadUsers();
          users = company.users;
        } else {
          users = await User.get() as User[];
        }
  
        const service = curriculum_type === DataType.Course ? Services.CourseUser : Services.ProgramUser;
  
        const content_users: Paginated<ICourseUser | IProgramUser> = await client.service(service).find({
          query: {
            [curriculum_type + "_id"]: curriculum.id
          },
        });
        return content_users.data.map(cu => {
          return users.find(u => Number(u.id) === Number(cu.user_id));
        }).filter(Boolean).sort((row1, row2) => row1.getFullName > row2.getFullName? 1 : -1);
      }
    } catch(err) {
      console.error("Failed to load participants", curriculum_type, curriculum, company_id);
      return [];
    }
  }

  /**
   * Get statistics for the manager
   * 
   * @config select which statistics to load
   */
  public loadStatistics = async (query: StatisticsQuery): Promise<boolean> => {
    try {
      const stats = await client.service(Services.Statistics).find({ query });
      runInAction(() => this.statistics = stats);
      return true;
    } catch(err) {
      console.error("Failed to load statistics", err);
      return false;
    }
  }

  public static loadSystemUsers = async (): Promise<User[]> => {
    try {
      const users: Paginated<IUser> = await client.service(Services.Users).find({
        query: {
          user_type: {$in: [UserType.Admin, UserType.Analyst, UserType.Editor]},
          $sort: {
            first_name: 1,
            last_name: 1,
          }
        },
      });
      return users.data.map(u => User.create(u));
    } catch(err) {
      console.error("Failed to load system users", err);
      return null;
    }
  }

  public static find = async (ids: number[]): Promise<User[]> => {
    try {
      return (await client.service(Services.Users).find({
        query: {
          id: {$in: ids}
        }
      }))?.data?.map((u: IUser) => User.create(u));
    } catch(err)  {
      console.error("Failed to load users", err);
      return [];
    }
  }

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/
  public static get = async (id?: number): Promise<User | User[]> => {
    try {
      if(id) {
        return User.create(await client.service(Services.Users).get(id));
      } else {
        const company_id = authStore.getCurUser.company?.id;
        const users: Paginated<IUser> = await client.service(Services.Users).find({
          query: {
            $sort: {
              first_name: 1,
              last_name: 1,
            }
          }
        }, company_id ? paramsForServer({company_id}) : undefined);
        if(!users.total) return [];
        return users.data.map(u => User.create(u));
      }
    } catch (err) {
      console.error("Failed to load users", err);
      return null;
    }
  }

  public static create(data: IUser): User {
    return new User(data);
  }

  protected fromData(data: IUser): void {
    super.fromData(data);
    this.email           = data.email;
    this.first_name      = data.first_name;
    this.last_name       = data.last_name;
    this.password        = data.password;
    this.personal_number = data.personal_number;
    this.phone           = data.phone;
    this.title           = data.title;
    this.user_type       = data.user_type;
    this.language        = data.language;
    this.progress        = data.progress;
    this.external_editor = data.external_editor;
  }

  public toData(): any {
    return {
      ...super.toData(),
      email:           this.email || null,
      first_name:      this.first_name || null,
      last_name:       this.last_name || null,
      password:        this.password || undefined,
      personal_number: this.personal_number || null,
      phone:           this.phone || null,
      title:           this.title || null,
      user_type:       this.user_type || null,
      language:        this.language || null,
      external_editor: this.external_editor || null,
    };
  }

  public toPatchData(patch: Partial<IUser>): any {
    return {
      ...super.toPatchData(patch),
      email:           "email" in patch ? patch.email: undefined,
      first_name:      "first_name" in patch ? patch.first_name: undefined,
      last_name:       "last_name" in patch ? patch.last_name: undefined,
      password:        "password" in patch ? patch.password: undefined,
      personal_number: "personal_number" in patch ? patch.personal_number: undefined,
      phone:           "phone" in patch ? patch.phone: undefined,
      title:           "title" in patch ? patch.title: undefined,
      user_type:       "user_type" in patch ? patch.user_type: undefined,
      language:        "language" in patch ? patch.language : undefined,
      external_editor: "external_editor" in patch ? patch.external_editor : undefined,
    };
  }

  public async save(patch?: Partial<IUser>, forwardError?: boolean): Promise<IUser> {
    try {
      // If the object already exists in the database, patch or update it
      let newData: IUser;
      if (this.id) {
        if (patch)
          newData = (await this.service.patch(this.id, this.toPatchData(patch))) as IUser;
        else
          newData = (await this.service.update(this.id, this.toData())) as IUser;
      } else {
        const company_id = authStore.getCurUser.company?.id;
        // If the objet does not exist, create it
        newData = (await this.service.create(
          {...this.toData(), id: undefined },
          authStore.getCurUser.company ? paramsForServer({company_id}) : undefined
        )) as IUser;
      }
      // Add the new @updated_at and possible @created_at and @id to the object. 
      // runInAction is need as this is an async call.
      if(!newData || !Object.keys(newData).length)  throw new Error("Service call returned empty");
      runInAction(() => this.fromData(newData)); 
      return newData;
    } catch (error) {
      if(forwardError) throw error;
      console.error("Failed to save object", this, error);
      return null;
    }
  }

  /*******************************************************************************************************************************************
   * Getter
   ********************************************************************************************************************************************/

  public get canEdit(): boolean {
    return true;
  }

  public get getFullName(): string {
    return [this.first_name, this.last_name].filter(Boolean).join(" ");
  }

  /*******************************************************************************************************************************************
   * Setter
   ********************************************************************************************************************************************/
  public changeLanguage = async (language: Language) => {
    try {
      await this.set("language", language, true);
      i18next.changeLanguage(language);
    } catch(err) {
      console.error("Failed to change language", language, err);
    }
  };

  public setCompany = async () => {
    if(this.user_type !== UserType.Manager) return;

    const companies: Company[] = await Company.get() as Company[];
    if(!companies || !companies.length) {
      throw new Error("Managers need a company");
    }
    this.company = companies[0];
  }

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/
  linkContent = async (content_type: DataType.Course | DataType.Package | DataType.Program, content_id: number, company_id?: number): Promise<boolean> => {
    switch(content_type) {
      case DataType.Course:
        return this.linkCourse(content_id, company_id);
      case DataType.Program:
        return this.linkProgram(content_id, company_id);
      case DataType.Package:
        return true;
    }
  }

  unlinkContent = async (content_type: DataType.Course | DataType.Package | DataType.Program, content_id: number, company_id?: number): Promise<boolean> => {
    switch(content_type) {
      case DataType.Course:
        return this.unlinkCourse(content_id, company_id);
      case DataType.Program:
        return this.unlinkProgram(content_id, company_id);
      case DataType.Package:
        return true;
    }
  }
  /**
   * Creates a course_user for @this user and the passed @course_id .
   * The backend takes care of license checks and license consumption.
   * 
   * Note: Without realtime updates, the information about available licenses
   * for the current user has to be loaded again!
   */
  linkCourse = async (course_id: number, company_id?: number): Promise<boolean> => {
    try {
      await client.service(Services.CourseUser).create({
        user_id:   this.id,
        course_id: course_id,
      }, paramsForServer({company_id}));
      return true;
    } catch(err) {
      console.error("Failed to create course_user", err);
      return false;
    }
  }
  unlinkCourse = async (course_id: number, company_id?: number): Promise<boolean> => {
    try {
      const course_user: Paginated<ICourseUser> = await client.service(Services.CourseUser).find({
        query: {
          user_id:   this.id,
          course_id: course_id,
        }
      });
      if(!course_user || !course_user.total) throw new Error("Course user not found.");
      await client.service(Services.CourseUser).remove(course_user.data[0].id, paramsForServer({company_id}));
      return true;
    } catch(err) {
      console.error("Failed to remove course_user", err);
      return false;
    }
  }

  /**
   * Creates a program_user for @this user and the passed @program_id .
   * The backend takes care of license checks and license consumption.
   * 
   * Note: Without realtime updates, the information about available licenses
   * for the current user has to be loaded again!
   */
  linkProgram = async (program_id: number, company_id?: number): Promise<boolean> => {
    try {
      await client.service(Services.ProgramUser).create({
        user_id:   this.id,
        program_id: program_id,
      }, paramsForServer({company_id}));
      return true;
    } catch(err) {
      console.error("Failed to create program_user", err);
      return false;
    }
  }
  unlinkProgram = async (program_id: number, company_id?: number): Promise<boolean> => {
    try {
      const program_user: Paginated<IProgramUser> = await client.service(Services.ProgramUser).find({
        query: {
          user_id:   this.id,
          program_id: program_id,
        }
      });
      if(!program_user || !program_user.total) throw new Error("Program user not found.");
      await client.service(Services.ProgramUser).remove(program_user.data[0].id, paramsForServer({company_id}));
      return true;
    } catch(err) {
      console.error("Failed to remove program_user", err);
      return false;
    }
  }


  /**
   * Creates a package_user for @this user and the passed @package_id .
   * The backend takes care of license checks and license consumption.
   * 
   * Note: Without realtime updates, the information about available licenses
   * for the current user has to be loaded again!
   */
  linkPackage = async (package_id: number): Promise<boolean> => {
    try {
      await client.service(Services.PackageUser).create({
        user_id:   this.id,
        package_id: package_id,
      });
      return true;
    } catch(err) {
      console.error("Failed to create package_user", err);
      return false;
    }
  }

  /**
   * Reset progres for that user for the specific course
   */
  resetProgress = async (course_id: number): Promise<boolean> => {
    try {
      const progress: Paginated<IProgress> = await client.service(Services.Progress).find({
        query: {
          user_id: this.id,
          course_id
        }
      });

      const course_user = await client.service(Services.CourseUser).find({
        query: {
          user_id: this.id,
          course_id,
        }
      });

      await client.service(Services.CourseUser).patch(course_user.data[0].id, {
        completed: null,
        progress:  0,
        attempts:  0,
      });

      if(!progress?.total) return true;

      for(const p of progress.data) {
        if(Number(p.user_id) === this.id && Number(p.course_id) === Number(course_id))
          await client.service(Services.Progress).remove(p.id);
        else throw new Error("Attempt to delete incorrect progress value!");
      }
      

      return true;
    } catch(err) {
      console.error("Failed to reset progress", err);
      return false;
    }
  }
}
//#endregion
